import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import {
  Newspaper, ArrowLeft, RefreshCw, ExternalLink, Clock, Tag as TagIcon,
  AlertCircle, TrendingUp, Lightbulb, CarFront, Battery, Cpu, Factory,
  BookOpen, ShieldCheck, Leaf, Sparkles, Info, FileText, Globe, Flag,
} from "lucide-react";
import { toast } from "sonner";
import SEOHead from "@/components/SEOHead";
import NewsletterSubscribe from "@/components/NewsletterSubscribe";
import {
  schemaBreadcrumb,
  schemaNewsArticle,
  combineSchemas,
} from "@/lib/siteSchemas";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// 8 rubriche editoriali: ogni rubrica ha icona + colore dedicato
const RUBRICA_ICON = {
  noleggio:     CarFront,
  novita_motor: Sparkles,
  elettrico:    Battery,
  tecnologia:   Cpu,
  industria:    Factory,
  guide:        BookOpen,
  sicurezza:    ShieldCheck,
  green:        Leaf,
};

const RUBRICA_LABEL = {
  noleggio:     "Noleggio & Flotte",
  novita_motor: "Novità Modelli",
  elettrico:    "Elettrico & Ibrido",
  tecnologia:   "Tecnologia & ADAS",
  industria:    "Industria & Mercato",
  guide:        "Guide & Consigli",
  sicurezza:    "Sicurezza Stradale",
  green:        "Mobilità Sostenibile",
};

const RUBRICA_COLOR = {
  noleggio:     "text-[#D97B58] bg-[#D97B58]/10 border-[#D97B58]/30",
  novita_motor: "text-[#163321] bg-[#163321]/10 border-[#163321]/30",
  elettrico:    "text-emerald-700 bg-emerald-50 border-emerald-200",
  tecnologia:   "text-indigo-700 bg-indigo-50 border-indigo-200",
  industria:    "text-zinc-700 bg-zinc-50 border-zinc-200",
  guide:        "text-amber-700 bg-amber-50 border-amber-200",
  sicurezza:    "text-rose-700 bg-rose-50 border-rose-200",
  green:        "text-teal-700 bg-teal-50 border-teal-200",
};

export default function NovitaNoleggioPage() {
  const [data, setData] = useState(null);
  const [activeRub, setActiveRub] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchNews = useCallback(async (rub = "", forceRefresh = false) => {
    setLoading(true); setError(null);
    try {
      const params = {};
      if (rub) params.rubrica = rub;
      if (forceRefresh) params.refresh = true;
      const { data } = await axios.get(`${API}/news-noleggio`, { params, withCredentials: true });
      setData(data);
    } catch (e) {
      setError(e?.response?.data?.detail || "Errore caricamento articoli");
      toast.error(e?.response?.data?.detail || "Errore caricamento articoli");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchNews(activeRub, false); }, [fetchNews, activeRub]);

  // Auto-refresh ogni 2 minuti: così quando lo scheduler AI giornaliero rigenera,
  // la pagina mostra i nuovi articoli senza bisogno di ricaricare manualmente.
  useEffect(() => {
    const interval = setInterval(() => {
      fetchNews(activeRub, false);
    }, 2 * 60 * 1000);  // 2 minuti
    return () => clearInterval(interval);
  }, [fetchNews, activeRub]);

  const fmtUpdated = (iso) => iso ? new Date(iso).toLocaleString("it-IT", { day: "2-digit", month: "long", hour: "2-digit", minute: "2-digit" }) : "";

  const articoli = data?.articoli || [];
  const rubricheMeta = data?.rubriche || [];
  const fontiConsultate = data?.fonti_consultate || [];

  return (
    <>
      <SEOHead
        title="Novità Noleggio, Auto & Mobilità | Redazione Colucci"
        description="Magazine editoriale aggiornato ogni ora: novità noleggio lungo termine, auto elettriche, tecnologia ADAS, industria automotive, guide pratiche. Fonti tracciabili da 13 testate italiane e internazionali."
        canonical="/noleggio/novita"
        keywords={[
          "novità noleggio lungo termine",
          "news auto elettriche",
          "magazine automotive italia",
          "guide NLT",
          "mobilità sostenibile",
        ]}
        jsonLd={[
          {
            "@context": "https://schema.org",
            "@type": "CollectionPage",
            name: "Novità Noleggio, Auto & Mobilità",
            description:
              "Magazine editoriale aggiornato ogni ora con analisi dal settore noleggio lungo termine, mobilità elettrica, nuovi modelli e industria automotive.",
            url: "https://www.colucciassicuratori.com/noleggio/novita",
            publisher: {
              "@type": "Organization",
              name: "Colucci Assicuratori",
              url: "https://www.colucciassicuratori.com/",
            },
            dateModified: data?.updated_at || new Date().toISOString(),
            inLanguage: "it-IT",
          },
          schemaBreadcrumb([
            { name: "Home", path: "/" },
            { name: "Noleggio", path: "/noleggio" },
            { name: "Novità", path: "/noleggio/novita" },
          ]),
          // ItemList dei top 10 articoli per permettere a Google di mostrare sitelinks
          ...(Array.isArray(data?.articoli) && data.articoli.length
            ? [
                {
                  "@context": "https://schema.org",
                  "@type": "ItemList",
                  itemListElement: data.articoli.slice(0, 10).map((art, i) => ({
                    "@type": "ListItem",
                    position: i + 1,
                    url: `https://www.colucciassicuratori.com/noleggio/novita/${art.slug}`,
                    name: art.titolo,
                  })),
                },
              ]
            : []),
        ]}
      />
      <Header />
      <main className="min-h-screen bg-[#FCFCFA] pt-20" data-testid="novita-noleggio-page">
        {/* HERO editoriale */}
        <section className="relative bg-gradient-to-br from-[#0F1F14] via-[#163321] to-[#1d4a2e] text-white overflow-hidden">
          <div className="absolute inset-0 opacity-[0.04]" style={{ backgroundImage: "radial-gradient(circle at 1px 1px, white 1px, transparent 0)", backgroundSize: "24px 24px" }} />
          <div className="absolute -top-40 -left-40 w-96 h-96 rounded-full bg-[#D97B58]/10 blur-3xl" />
          <div className="relative max-w-5xl mx-auto px-5 sm:px-6 py-14">
            <Link to="/noleggio" className="inline-flex items-center gap-1.5 text-xs text-white/60 hover:text-white mb-6 transition-colors" data-testid="back-to-catalog">
              <ArrowLeft className="w-3.5 h-3.5" /> Catalogo Noleggio
            </Link>
            <div className="inline-flex items-center gap-2 px-3 py-1 bg-white/10 backdrop-blur-sm border border-white/20 rounded-full text-[10px] font-semibold uppercase tracking-wider text-[#D97B58] mb-4">
              <Newspaper className="w-3 h-3" /> Centro informazione Motor & Noleggio
            </div>
            <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold leading-tight mb-3" data-testid="novita-title">
              Il magazine <span className="text-[#D97B58]">editoriale</span> di Colucci Solutions
            </h1>
            <p className="text-base text-white/80 max-w-2xl leading-relaxed mb-5">
              Selezione quotidiana dalle principali testate automotive italiane e internazionali. Analisi ragionata, lettura pratica per chi valuta un NLT o gestisce una flotta, <strong className="text-white">sempre con fonti tracciabili</strong>.
            </p>
            <div className="flex items-center gap-4 text-[11px] text-white/60 flex-wrap">
              {data?.updated_at && (
                <span className="flex items-center gap-1" data-testid="novita-updated">
                  <Clock className="w-3 h-3" /> Aggiornato il {fmtUpdated(data.updated_at)}
                </span>
              )}
              {fontiConsultate.length > 0 && (
                <span className="flex items-center gap-1">
                  <Globe className="w-3 h-3" /> {fontiConsultate.length} testate monitorate
                </span>
              )}
              <span className="flex items-center gap-1">
                <Sparkles className="w-3 h-3" /> 8 rubriche editoriali
              </span>
              <span className="flex items-center gap-1 text-white/50">
                <RefreshCw className="w-3 h-3" /> Aggiornamento automatico ogni 2 min
              </span>
            </div>
          </div>
        </section>

        <div className="max-w-5xl mx-auto px-5 sm:px-6 py-10">
          {/* Action bar: refresh + info */}
          <div className="flex items-start justify-between gap-4 mb-6 flex-wrap">
            <div className="flex items-center gap-2 text-xs text-[#4A5E51]">
              <Newspaper className="w-4 h-4 text-[#163321]" />
              <span>
                Ogni mattina la redazione AI seleziona le notizie piu' rilevanti e le commenta con lo sguardo di un consulente Colucci.
              </span>
            </div>
            <Button variant="outline" size="sm" onClick={() => fetchNews(activeRub, true)} disabled={loading} className="rounded-full border-[#E5E2DB]" data-testid="novita-refresh-btn">
              <RefreshCw className={`w-4 h-4 mr-2 ${loading ? "animate-spin" : ""}`} /> Aggiorna
            </Button>
          </div>

          {/* FILTRO RUBRICHE */}
          <div className="flex items-center gap-2 flex-wrap mb-8" data-testid="rubriche-filter">
            <button
              onClick={() => setActiveRub("")}
              className={`px-4 py-2 rounded-full text-xs font-semibold transition-all border ${activeRub === "" ? "bg-[#163321] text-white border-[#163321]" : "bg-white text-[#4A5E51] border-[#E5E2DB] hover:border-[#163321]"}`}
              data-testid="rubrica-all"
            >
              Tutte le rubriche
            </button>
            {rubricheMeta.map(r => {
              const Icon = RUBRICA_ICON[r.value] || Newspaper;
              const active = activeRub === r.value;
              const count = typeof r.count === "number" ? r.count : null;
              return (
                <button
                  key={r.value}
                  onClick={() => setActiveRub(r.value)}
                  className={`px-4 py-2 rounded-full text-xs font-semibold transition-all border inline-flex items-center gap-1.5 ${active ? "bg-[#163321] text-white border-[#163321]" : "bg-white text-[#4A5E51] border-[#E5E2DB] hover:border-[#163321]"}`}
                  data-testid={`rubrica-${r.value}`}
                >
                  <Icon className="w-3.5 h-3.5" />
                  {r.label || RUBRICA_LABEL[r.value] || r.value}
                  {count !== null && <span className="text-[10px] opacity-70 ml-0.5">({count})</span>}
                </button>
              );
            })}
          </div>

          {/* DISCLAIMER METODOLOGICO */}
          <div className="bg-[#FFF7E8] border border-[#E8D3A0] rounded-2xl p-4 mb-8 flex items-start gap-3" data-testid="novita-disclaimer">
            <Info className="w-5 h-5 text-[#B8820E] shrink-0 mt-0.5" />
            <p className="text-xs text-[#8B6100] leading-relaxed">
              <strong>Nota metodologica.</strong> Gli articoli sono elaborati con supporto di intelligenza artificiale a partire dai feed RSS ufficiali delle principali testate automotive italiane e internazionali. <strong>Ogni articolo riporta la fonte originale con link diretto</strong>; i dati tecnici e le dichiarazioni virgolettate provengono esclusivamente dalle testate citate. Le letture Colucci Solutions e i suggerimenti operativi hanno natura <strong>esclusivamente informativa</strong> e non sostituiscono la consulenza personalizzata. Per valutazioni concrete su un NLT o una flotta, contatta la nostra agenzia.
            </p>
          </div>

          {/* LOADING */}
          {loading && !data && (
            <div className="flex flex-col items-center justify-center py-20 gap-3" data-testid="novita-loading">
              <div className="w-10 h-10 border-2 border-[#163321] border-t-transparent rounded-full animate-spin" />
              <p className="text-xs text-[#4A5E51]">Caricamento articoli dalle testate monitorate...</p>
            </div>
          )}

          {/* ERRORE */}
          {error && !data && (
            <div className="bg-white border border-red-200 rounded-2xl p-8 text-center" data-testid="novita-error">
              <AlertCircle className="w-8 h-8 text-red-500 mx-auto mb-3" />
              <p className="text-sm text-[#0F1F14] font-medium mb-1">Non riusciamo a recuperare gli articoli in questo momento</p>
              <p className="text-xs text-[#4A5E51]">{error}</p>
              <Button onClick={() => fetchNews(activeRub, false)} className="mt-4 bg-[#163321] text-white rounded-full" data-testid="novita-retry">Riprova</Button>
            </div>
          )}

          {/* WARMING UP (cache vuota) */}
          {data?.warming && articoli.length === 0 && !loading && (
            <div className="bg-white border border-[#E5E2DB] rounded-2xl p-10 text-center" data-testid="novita-warming">
              <Sparkles className="w-10 h-10 text-[#D97B58]/60 mx-auto mb-3" />
              <p className="font-heading text-lg text-[#0F1F14] font-medium mb-2">La redazione sta preparando i primi articoli</p>
              <p className="text-sm text-[#4A5E51] max-w-md mx-auto">{data.message || "Il centro informazione sarà popolato alla prossima rigenerazione giornaliera (07:00)."}</p>
            </div>
          )}

          {/* WARNING cache stale */}
          {data?.warning && (
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 mb-5 text-xs text-amber-800 flex items-center gap-2">
              <AlertCircle className="w-4 h-4" /> {data.warning}
            </div>
          )}

          {/* LISTA ARTICOLI — stile editoriale */}
          {articoli.length > 0 && (
            <div className="space-y-5" data-testid="novita-list">
              {articoli.map((a, i) => (
                <ArticoloEditorial key={a.slug || i} art={a} index={i} />
              ))}
            </div>
          )}

          {/* EMPTY per rubrica senza risultati */}
          {data && !data.warming && articoli.length === 0 && !loading && !error && (
            <div className="bg-white border border-[#E5E2DB] rounded-2xl p-10 text-center text-sm text-[#4A5E51]" data-testid="novita-empty">
              Nessun articolo disponibile per questa rubrica al momento. Prova a cambiare rubrica o torna più tardi.
            </div>
          )}

          {/* FOOTER — fonti consultate */}
          {fontiConsultate.length > 0 && (
            <div className="mt-12 pt-6 border-t border-[#E5E2DB]" data-testid="novita-fonti-footer">
              <p className="text-[10px] uppercase tracking-wider text-[#4A5E51] font-bold mb-2 flex items-center gap-1.5">
                <Globe className="w-3 h-3" /> Testate monitorate dal centro informazione
              </p>
              <p className="text-[11px] text-[#4A5E51] leading-relaxed">
                {fontiConsultate.join(" · ")}
              </p>
              <p className="text-[11px] text-[#4A5E51] mt-3">
                Vuoi una consulenza personalizzata su un NLT?{" "}
                <Link to="/noleggio" className="text-[#163321] underline">Scopri il catalogo</Link>
                {" "}oppure chiama lo{" "}
                <a href="tel:0817611000" className="text-[#163321] underline">081 761 1000</a>.
              </p>
            </div>
          )}

          {/* NEWSLETTER — iscrizione digest settimanale */}
          <div className="mt-12">
            <NewsletterSubscribe fonte="news-noleggio" />
          </div>
        </div>
      </main>
      <Footer />
    </>
  );
}

function ArticoloEditorial({ art, index }) {
  const Icon = RUBRICA_ICON[art.rubrica] || Newspaper;
  const rubLabel = RUBRICA_LABEL[art.rubrica] || art.rubrica;
  const rubColor = RUBRICA_COLOR[art.rubrica] || RUBRICA_COLOR.novita_motor;
  const isItalian = art.fonte_nome && !/\b(UK|US|FR|DE|EU)\b/i.test(art.fonte_nome);

  return (
    <article className="bg-white border border-[#E5E2DB] rounded-2xl overflow-hidden hover:border-[#163321]/30 transition-colors" data-testid={`novita-articolo-${index}`}>
      {/* Hero image (opzionale) */}
      {art.immagine_url && (
        <Link to={`/noleggio/novita/${art.slug}`} className="block aspect-[21/9] bg-zinc-100 overflow-hidden relative group">
          <img
            src={art.immagine_url}
            alt={art.titolo}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
            loading="lazy"
            onError={(e) => { e.currentTarget.style.display = "none"; }}
          />
        </Link>
      )}

      <div className="p-6">
        {/* Header: rubrica + tag + rilevanza + fonte */}
        <div className="flex items-start justify-between gap-4 mb-3 flex-wrap">
          <div className="flex items-center gap-2 flex-wrap">
            <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium border ${rubColor}`} data-testid={`novita-rubrica-${index}`}>
              <Icon className="w-3.5 h-3.5" /> {rubLabel}
            </span>
            {art.rilevanza === "alta" && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider bg-[#163321] text-white">
                <TrendingUp className="w-3 h-3" /> Alta rilevanza
              </span>
            )}
            {art.tag && (
              <span className="inline-flex items-center gap-1 text-xs text-[#4A5E51]">
                <TagIcon className="w-3 h-3" /> {art.tag}
              </span>
            )}
          </div>
          <span className="text-[11px] text-[#4A5E51] uppercase tracking-wider flex items-center gap-1">
            {isItalian ? <Flag className="w-3 h-3" /> : <Globe className="w-3 h-3" />}
            {art.fonte_nome || "Fonte originale"}
          </span>
        </div>

        {/* Titolo */}
        <Link to={`/noleggio/novita/${art.slug}`} className="block group">
          <h2 className="font-heading text-xl text-[#0F1F14] font-medium leading-tight mb-3 group-hover:text-[#163321] transition-colors">
            {art.titolo}
          </h2>
        </Link>

        {/* Sommario */}
        {art.sommario && (
          <p className="text-sm text-[#4A5E51] leading-relaxed mb-5 italic">{art.sommario}</p>
        )}

        {/* Lettura Colucci Solutions */}
        {art.commento_colucci && (
          <div className="bg-[#F5F8F6] border border-[#163321]/15 rounded-xl p-4 mb-4" data-testid={`novita-commento-${index}`}>
            <p className="text-[10px] uppercase tracking-wider text-[#163321] font-bold mb-2 flex items-center gap-1">
              <Newspaper className="w-3 h-3" /> Lettura Colucci Solutions
            </p>
            <p className="text-sm text-[#0F1F14] leading-relaxed whitespace-pre-wrap">{art.commento_colucci}</p>
          </div>
        )}

        {/* Cosa valutare */}
        {art.suggerimento && (
          <div className="bg-[#FFF8E1] border border-[#E8D3A0]/60 rounded-xl p-4 mb-4" data-testid={`novita-suggerimento-${index}`}>
            <p className="text-[10px] uppercase tracking-wider text-[#8B6100] font-bold mb-2 flex items-center gap-1">
              <Lightbulb className="w-3 h-3" /> Cosa valutare
            </p>
            <p className="text-sm text-[#0F1F14] leading-relaxed">{art.suggerimento}</p>
          </div>
        )}

        {/* Fonti citate */}
        {Array.isArray(art.fonti_dati) && art.fonti_dati.length > 0 && (
          <div className="mb-4 pt-3 border-t border-dashed border-[#E5E2DB]" data-testid={`novita-fonti-${index}`}>
            <p className="text-[10px] uppercase tracking-wider text-[#4A5E51] font-bold mb-2 flex items-center gap-1">
              <FileText className="w-3 h-3" /> Fonti citate nell'analisi
            </p>
            <ul className="space-y-1">
              {art.fonti_dati.map((f, idx) => (
                <li key={idx} className="text-[11px] text-[#4A5E51] flex items-start gap-1.5">
                  <span className="text-[#163321] mt-0.5">•</span>
                  <span className="italic">{f}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Link fonte + leggi tutto */}
        <div className="flex items-center justify-between gap-4 flex-wrap pt-2">
          {art.fonte_link && (
            <a
              href={art.fonte_link}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 text-xs text-[#163321] hover:underline font-medium"
              data-testid={`novita-fonte-link-${index}`}
            >
              <ExternalLink className="w-3.5 h-3.5" /> Leggi la fonte originale ({art.fonte_nome || "sito ufficiale"})
            </a>
          )}
          <Link
            to={`/noleggio/novita/${art.slug}`}
            className="inline-flex items-center gap-1 text-xs text-[#D97B58] hover:text-[#163321] font-semibold transition-colors"
            data-testid={`novita-leggi-${index}`}
          >
            Leggi l'analisi completa →
          </Link>
        </div>
      </div>
    </article>
  );
}
